import React, { useEffect, useRef } from "react"
import clsx from "clsx"

import { useTranslation } from "react-i18next"

import { EnumTimePeriod, getLimitedTimeframeOptions, getTimePeriod, TimePeriodOptions } from "util/dashboard"

interface CustomCalendarContainerProps {
  className?: string
  children?: React.ReactNode
  pendingTimeframe: (Date | null)[]
  extendedPresetOptions?: boolean
  setPendingTimeframe: (dates: (Date | null)[]) => void
  applyPendingTimeframe: () => void
  clearPendingTimeframe: () => void
  closePicker: () => void
  markPresetSelection: () => void
}

export const CustomCalendarContainer = ({
  className,
  children,
  pendingTimeframe,
  extendedPresetOptions,
  setPendingTimeframe,
  applyPendingTimeframe,
  clearPendingTimeframe,
  closePicker,
  markPresetSelection
}: CustomCalendarContainerProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const { t } = useTranslation("translation", { useSuspense: false })

  const triggerDropdowns = () => {
    const monthDropdown = containerRef.current?.querySelector(
      ".react-datepicker__month-dropdown-container--scroll .react-datepicker__month-read-view"
    ) as HTMLElement | null
    const yearDropdown = containerRef.current?.querySelector(
      ".react-datepicker__year-dropdown-container--scroll .react-datepicker__year-read-view"
    ) as HTMLElement | null

    monthDropdown?.click()
    yearDropdown?.click()
  }

  useEffect(() => {
    const header = containerRef.current?.querySelector(".react-datepicker__current-month") as HTMLElement | null
    if (!header) return

    header.style.cursor = "pointer"

    const onClick = (e: MouseEvent) => {
      e.stopPropagation()
      triggerDropdowns()
    }

    header.addEventListener("click", onClick)
    return () => header.removeEventListener("click", onClick)
  }, [])

  const handleCancel = () => {
    clearPendingTimeframe()
    closePicker()
  }

  const handleApply = () => {
    applyPendingTimeframe()
    closePicker()
  }

  const renderPresetOptions = extendedPresetOptions
    ? TimePeriodOptions.filter((opt) => opt.value !== EnumTimePeriod.custom).map((opt) => {
        const [start, end] = getTimePeriod(opt.value as EnumTimePeriod)
        return (
          <PresetButton
            key={opt.value}
            label={t(opt.name)}
            start={start}
            end={end}
            setPendingTimeframe={setPendingTimeframe}
            markPresetSelection={markPresetSelection}
          />
        )
      })
    : getLimitedTimeframeOptions().map((opt, idx) => (
        <PresetButton
          key={idx}
          label={t(opt.label)}
          start={opt.start}
          end={opt.end}
          setPendingTimeframe={setPendingTimeframe}
          markPresetSelection={markPresetSelection}
        />
      ))

  return (
    <div className="flex flex-row sm:flex-col rounded-lg bg-[rgba(6,7,21,1)]">
      <div className="flex flex-row">
        <div
          className={clsx("flex flex-col gap-2 w-full sm:w-40 py-5 px-4 border border-[rgba(25,26,39,1)] border-r-0", {
            ["max-h-[370px] overflow-auto"]: extendedPresetOptions
          })}
        >
          {renderPresetOptions}
        </div>
        <div className="hidden sm:flex">
          <div ref={containerRef} className={className}>
            {children}
          </div>
        </div>
      </div>

      <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 p-4 text-sm border-t border-[rgba(25,26,39,1)]">
        <button
          onClick={handleCancel}
          className="px-5 py-2 rounded-lg border border-[rgba(255,255,255,0.2)] text-[rgba(127,130,164,1)] hover:border-white hover:text-white transition"
        >
          {t("common.cancel")}
        </button>

        <button
          onClick={handleApply}
          disabled={!pendingTimeframe[0] || !pendingTimeframe[1]}
          className="px-5 py-2 rounded-lg transition-all bg-[rgba(132,0,168,1)] text-white hover:bg-[rgba(132,0,168,0.7)] disabled:opacity-50"
        >
          {t("common.apply")}
        </button>
      </div>
    </div>
  )
}

const PresetButton = ({
  label,
  start,
  end,
  setPendingTimeframe,
  markPresetSelection
}: {
  label: string
  start: Date
  end: Date
  setPendingTimeframe: (dates: (Date | null)[]) => void
  markPresetSelection: () => void
}) => {
  const handleClick = () => {
    markPresetSelection()
    setPendingTimeframe([start, end])
  }

  return (
    <button
      onClick={handleClick}
      className="text-sm text-left p-2 pl-3 rounded-lg transition hover:bg-[rgba(218,83,255,0.1)] hover:text-[rgba(217,77,255,1)]"
    >
      {label}
    </button>
  )
}
